import { ReactNode } from 'react';
import { Box, SxProps, Typography, TypographyProps } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useCallback, useMemo } from 'react';

import { formatMoneyAmount } from '@occc-app/lib/currency';

import { TrendBadge, TrendBadgeDelta } from '../trend-badge';
import { MetricTooltip } from '../metric-tooltip';

const NOT_AVAILABLE_LABEL = 'N/A';

const isInvalidValue = (v: number) => isNaN(v) || !isFinite(v);
const isNoValue = (v: number) => !v || isInvalidValue(v);

export type MetricCardValueType = 'number' | 'currency' | 'cents' | 'percentage';

export type MetricCardProps<T = unknown> = {
  label: string;
  value: number;
  valuePrefix?: string;
  valueSuffix?: string;
  valueType?: MetricCardValueType;
  valueTypographyProps?: TypographyProps;
  valuePrefixTypographyProps?: TypographyProps;
  valueSuffixTypographyProps?: TypographyProps;
  prevValue?: number;
  isTrendHidden?: boolean;
  tooltip?: string | ReactNode;
  onClick?: (metricCard: MetricCardProps<T>) => void;
  payload?: T;
  sx?: SxProps;
  isLabelNotWrapped?: boolean;
  isNoHoverEffect?: boolean;
};

export const MetricCard = <T,>(props: MetricCardProps<T>) => {
  const {
    label,
    valuePrefix,
    valueSuffix,
    value,
    prevValue,
    valueType = 'number',
    valueTypographyProps,
    valuePrefixTypographyProps,
    valueSuffixTypographyProps,
    isTrendHidden,
    tooltip,
    onClick,
    sx,
    isLabelNotWrapped,
    isNoHoverEffect,
  } = props;

  const formattedValue = useMemo(() => {
    if (valueType === 'currency' || valueType === 'cents') {
      return formatMoneyAmount(value, {
        fromCents: valueType === 'cents',
        showCents: value < (valueType === 'cents' ? 100000 : 1000),
      });
    }

    const formatted = new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2,
    }).format(value);

    return valueType === 'percentage' ? `${value > 0 ? '+' : ''}${formatted}%` : formatted;
  }, [value, valueType]);

  const trendBadge = useMemo(() => {
    if (isTrendHidden) {
      return null;
    }
    return valueType === 'percentage' ? (
      <TrendBadge
        color={isNoValue(value) ? 'gray' : value > 0 ? 'green' : 'red'}
        arrow={isNoValue(value) ? undefined : value > 0 ? 'up' : 'down'}
        label={isInvalidValue(value) ? NOT_AVAILABLE_LABEL : formattedValue.replace(/^[-+]/, '')}
      />
    ) : (
      <TrendBadgeDelta
        value={value}
        prevValue={prevValue}
        notAvailableLabel={NOT_AVAILABLE_LABEL}
      />
    );
  }, [isTrendHidden, valueType, value, formattedValue, prevValue]);

  const tooltipIcon = useMemo(() => {
    if (!tooltip) {
      return null;
    }
    return (
      <MetricTooltip title={tooltip} placement='top'>
        <InfoOutlinedIcon fontSize='medium' sx={{ color: 'silver' }} />
      </MetricTooltip>
    );
  }, [tooltip]);

  const style = useMemo(() => {
    const defaultStyle = { height: '100%' }; // if we use few components in a row, they should have the same height and same alignment

    if (!onClick) {
      return defaultStyle;
    }

    const currentStyle = {
      ...defaultStyle,
      transform: 'scale(1)',
      transformOrigin: 'center',
      transition: 'transform 0.3s ease',
      cursor: 'pointer',
      userSelect: 'none',
    };

    if (!isNoHoverEffect) {
      return {
        ...currentStyle,
        '&:hover': {
          transform: 'scale(0.95)',
        },
      };
    }

    return currentStyle;
  }, [isNoHoverEffect, onClick]);

  const click = useCallback(() => {
    onClick?.(props);
  }, [onClick, props]);

  return (
    <Box sx={sx}>
      <Box display='inline-flex' flexDirection='column' gap={0.5} onClick={click} sx={style}>
        {label ? (
          <Typography
            variant='body2'
            color='textSecondary'
            flex={1}
            sx={isLabelNotWrapped ? { whiteSpace: 'nowrap' } : undefined}
          >
            {label}
          </Typography>
        ) : null}
        <Box display='flex' alignItems='center' gap={1}>
          {isInvalidValue(value) ? (
            <Typography variant='h4' fontWeight={400} noWrap {...valueTypographyProps}>
              {NOT_AVAILABLE_LABEL}
            </Typography>
          ) : (
            <Typography variant='h4' fontWeight={400} noWrap {...valueTypographyProps}>
              {valuePrefix && (
                <Typography
                  variant='h4'
                  fontWeight={400}
                  component='span'
                  {...valuePrefixTypographyProps}
                >
                  {valuePrefix}
                </Typography>
              )}
              {formattedValue}
              {valueSuffix && (
                <Typography
                  variant='h4'
                  fontWeight={400}
                  component='span'
                  {...valueSuffixTypographyProps}
                >
                  {valueSuffix}
                </Typography>
              )}
            </Typography>
          )}
          {trendBadge}
          {tooltipIcon}
        </Box>
      </Box>
    </Box>
  );
};
